import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaBriefcase,
  FaExternalLinkAlt,
  FaGavel,
  FaFileAlt,
  FaBuilding,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaListUl,
  FaCircleNotch,
  FaExclamationTriangle,
  FaCheckCircle,
} from 'react-icons/fa';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import Table from '@frontend/Components/Table/Table';
import FilterDropdown from '@frontend/Components/FilterDropdown/FilterDropdown';
import type { Column } from '@frontend/Models/Table/TableModels';
import ClientMattersService from '@frontend/AppSettings/services/ClientMattersService';
import ClientService from '@frontend/AppSettings/services/ClientService';
import AgentsService from '@frontend/AppSettings/services/AgentsService';
import { isLegalEnvironment } from '@frontend/AppSettings/environmentConfig';
import './StakeholderMatters.css';

interface StakeholderMattersProps {
  stakeholderId: string;
  type: 'individual' | 'organization';
}

const StakeholderMatters: React.FC<StakeholderMattersProps> = ({ stakeholderId, type }) => {
  const [matters, setMatters] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [leaderFilter, setLeaderFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<string>('all');

  const entityLabel = isLegalEnvironment ? 'Matter' : 'Project';
  const entityDetailsPath = isLegalEnvironment ? '/matterDetails' : '/projectDetails';
  const entitySearchTerm = isLegalEnvironment ? 'matters' : 'projects';

  const fetchMatters = useCallback(async () => {
    setLoading(true);
    try {
      if (!stakeholderId) {
        setMatters([]);
        return;
      }

      let allMatters: any[] = [];

      if (type === 'organization') {
        // For organizations, extract the actual ID and find all clients/agents with the same company
        const actualId = stakeholderId.startsWith('org-') 
          ? stakeholderId.replace('org-', '') 
          : stakeholderId;
        
        const clients = await ClientService.getClients();
        const agents = await AgentsService.getAgents();
        
        // Find the organization's company name
        let companyName: string | null = null;
        let foundClient = clients.find((c: any) => String(c.ClientID) === actualId);
        if (foundClient && foundClient.Company) {
          companyName = foundClient.Company;
        } else {
          let foundAgent = agents.find((a: any) => String(a.AgentID) === actualId);
          if (foundAgent && foundAgent.Company) {
            companyName = foundAgent.Company;
          }
        }

        if (companyName) {
          // Get all client IDs with this company name (as strings for API)
          const orgClientIds = clients
            .filter((c: any) => c.Company === companyName)
            .map((c: any) => String(c.ClientID));
          
          // Fetch matters for all clients in the organization
          const mattersPromises = orgClientIds.map(async (clientId: string) => {
            try {
              return await ClientMattersService.getClientMatters(clientId);
            } catch (error) {
              console.error(`Error fetching matters for client ${clientId}:`, error);
              return [];
            }
          });
          
          const mattersArrays = await Promise.all(mattersPromises);
          allMatters = mattersArrays.flat();
        }
      } else {
        // For individuals, use the stakeholderId directly
        const responseData = await ClientMattersService.getClientMatters(stakeholderId);
        allMatters = Array.isArray(responseData) ? responseData : [];
      }

      // Ensure allMatters is an array before processing
      if (!Array.isArray(allMatters)) {
        allMatters = [];
      }

      // Remove duplicates and sort
      const uniqueMatters = Array.from(
        new Map(allMatters.map((m: any) => [m.matterid, m])).values()
      );
      
      const sortedData = uniqueMatters.sort((a: any, b: any) => {
        const numA = parseInt(a.matterid?.split('-').pop() || '0', 10);
        const numB = parseInt(b.matterid?.split('-').pop() || '0', 10);
        return numA - numB;
      });
      setMatters(sortedData);
    } catch (error) {
      console.error('Error fetching stakeholder matters:', error);
      setMatters([]);
    } finally {
      setLoading(false);
    }
  }, [stakeholderId, type]);

  const toggleSort = (key: string) => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir('asc');
      return;
    }
    setSortDir((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));
  };

  const getMatterTypeIcon = (matterType?: string) => {
    if (!matterType) return FaBriefcase;
    const typeLower = matterType.toLowerCase();
    if (typeLower.includes('corporate') || typeLower.includes('business')) return FaBriefcase;
    if (typeLower.includes('legal') || typeLower.includes('dispute') || typeLower.includes('litigation')) return FaGavel;
    if (typeLower.includes('real estate') || typeLower.includes('property')) return FaBuilding;
    return FaFileAlt;
  };

  const getMatterTypeColor = (matterType?: string) => {
    if (!matterType) return '#6f42c1';
    const typeLower = matterType.toLowerCase();
    if (typeLower.includes('corporate') || typeLower.includes('business')) return '#6f42c1';
    if (typeLower.includes('legal') || typeLower.includes('dispute') || typeLower.includes('litigation')) return '#fd7e14';
    if (typeLower.includes('real estate') || typeLower.includes('property')) return '#6f42c1';
    return '#1a1a2e';
  };

  const formatDueDate = (dateString?: string) => {
    if (!dateString) return { date: 'N/A', status: '', color: '#6c757d' };

    const dueDate = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);

    const diffTime = dueDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const formattedDate = dueDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    if (diffDays < 0) {
      return { date: formattedDate, status: `${Math.abs(diffDays)} days overdue`, color: '#dc3545' };
    }
    if (diffDays === 0) {
      return { date: formattedDate, status: 'Due today', color: '#ffc107' };
    }
    if (diffDays <= 7) {
      return { date: formattedDate, status: `${diffDays} days left`, color: '#ffc107' };
    }
    return { date: formattedDate, status: `${diffDays} days left`, color: '#6c757d' };
  };

  const isCompleted = (row: any) =>
    row.status && (row.status.toLowerCase().includes('complete') || row.status.toLowerCase().includes('closed'));

  const matchesDateFilter = (rawDate: string | undefined, filter: string) => {
    if (filter === 'all') return true;
    if (!rawDate) return false;
    const parsed = Date.parse(rawDate);
    if (Number.isNaN(parsed)) return false;

    const date = new Date(parsed);
    date.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (filter) {
      case 'today':
        return date.getTime() === today.getTime();
      case 'week': {
        const end = new Date(today);
        end.setDate(today.getDate() + 7);
        return date.getTime() >= today.getTime() && date.getTime() <= end.getTime();
      }
      case 'month': {
        const end = new Date(today);
        end.setMonth(today.getMonth() + 1);
        return date.getTime() >= today.getTime() && date.getTime() <= end.getTime();
      }
      case 'year': {
        const end = new Date(today);
        end.setFullYear(today.getFullYear() + 1);
        return date.getTime() >= today.getTime() && date.getTime() <= end.getTime();
      }
      case 'overdue':
        return date.getTime() < today.getTime();
      default:
        return true;
    }
  };

  const getSortValue = (key: string, row: any) => {
    switch (key) {
      case 'matter':
        return row.matterid || '';
      case 'status':
        return row.status || '';
      case 'leader':
        return row.employeename || '';
      case 'duedate':
        return row.closedate || row.due_date || '';
      case 'opendate':
        return row.opendate || '';
      default:
        return '';
    }
  };

  const filterOptions = useMemo(() => {
    if (!matters || matters.length === 0) {
      return { statuses: [], leaders: [], matterTypes: [] };
    }

    const statuses = new Set<string>();
    const leaders = new Set<string>();
    const matterTypes = new Set<string>();

    matters.forEach((matter) => {
      if (matter.status) statuses.add(matter.status);
      if (matter.employeename) leaders.add(matter.employeename);
      if (matter.mattertype) matterTypes.add(matter.mattertype);
    });

    return {
      statuses: Array.from(statuses).sort().map((status) => ({ value: status, label: status })),
      leaders: Array.from(leaders).sort().map((leader) => ({ value: leader, label: leader })),
      matterTypes: Array.from(matterTypes).sort().map((type) => ({ value: type, label: type })),
    };
  }, [matters]);

  const filteredMatters = useMemo(() => {
    let result = [...matters];

    if (statusFilter !== 'all') {
      result = result.filter((matter) => matter.status === statusFilter);
    }
    if (leaderFilter !== 'all') {
      result = result.filter((matter) => matter.employeename === leaderFilter);
    }
    if (typeFilter !== 'all') {
      result = result.filter((matter) => matter.mattertype === typeFilter);
    }
    if (dateFilter !== 'all') {
      result = result.filter((matter) => matchesDateFilter(matter.closedate || matter.due_date, dateFilter));
    }

    return result;
  }, [matters, statusFilter, leaderFilter, typeFilter, dateFilter]);

  const displayedMatters = useMemo(() => {
    if (!sortBy || !sortDir) return filteredMatters;
    const comparator = (a: any, b: any) => {
      const va = getSortValue(sortBy, a);
      const vb = getSortValue(sortBy, b);

      if (sortBy === 'opendate') {
        const da = Date.parse(va);
        const db = Date.parse(vb);
        if (!Number.isNaN(da) && !Number.isNaN(db)) {
          return da - db;
        }
      }
      return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    };
    const sorted = [...filteredMatters].sort(comparator);
    return sortDir === 'asc' ? sorted : sorted.reverse();
  }, [filteredMatters, sortBy, sortDir]);

  const SortHeader = ({ label, colKey }: { label: string; colKey: string }) => (
    <div
      onClick={() => toggleSort(colKey)}
      className="stakeholder-sort-header"
    >
      <span>{label}</span>
      <span className="stakeholder-sort-icons">
        {sortBy === colKey && sortDir === 'asc' && <FaSortUp />}
        {sortBy === colKey && sortDir === 'desc' && <FaSortDown />}
        {(sortBy !== colKey || sortDir === null) && <FaSort />}
      </span>
    </div>
  );

  useEffect(() => {
    fetchMatters();
  }, [fetchMatters]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const summary = useMemo(() => {
    if (!filteredMatters || filteredMatters.length === 0) {
      return { total: 0, inProgress: 0, overdue: 0, completed: 0 };
    }

    return filteredMatters.reduce(
      (acc, matter) => {
        const completed = isCompleted(matter);

        let dueDate: Date | null = null;
        const rawDue = matter.closedate || matter.due_date;
        if (rawDue) {
          const parsed = Date.parse(rawDue);
          if (!Number.isNaN(parsed)) {
            dueDate = new Date(parsed);
            dueDate.setHours(0, 0, 0, 0);
          }
        }

        const isOverdue = !completed && !!dueDate && dueDate.getTime() < today.getTime();

        if (completed) {
          acc.completed += 1;
        } else if (isOverdue) {
          acc.overdue += 1;
        } else {
          acc.inProgress += 1;
        }

        acc.total += 1;
        return acc;
      },
      { total: 0, inProgress: 0, overdue: 0, completed: 0 }
    );
  }, [filteredMatters, today]);

  const mattersColumns: Column<any>[] = [
    {
      header: <SortHeader label={entityLabel} colKey="matter" />,
      render: (matter: any) => (
        <div className="matter-name-cell">
          <div
            className="matter-icon-wrapper"
            style={{ backgroundColor: getMatterTypeColor(matter.mattertype) }}
          >
            {React.createElement(getMatterTypeIcon(matter.mattertype), {
              className: 'matter-type-icon',
            })}
          </div>
          <div className="matter-name-content">
            <div className="matter-name-text">
              <Link
                to={`${entityDetailsPath}/${matter.matterid}`}
                className="stakeholder-matter-link"
              >
                {matter.mattername || (isLegalEnvironment ? 'Untitled Matter' : 'Untitled Project')}
              </Link>
            </div>
            <div className="matter-id-text">
              {matter.matterid || 'N/A'}
            </div>
          </div>
          <FaExternalLinkAlt style={{ fontSize: '0.8rem', opacity: 0.6 }} />
        </div>
      ),
    },
    {
      header: <SortHeader label="Leader" colKey="leader" />,
      render: (matter: any) => {
        const leaderName = matter.employeename || 'Unassigned';
        const leaderTitle = matter.employee_title || matter.employeeTitle || 'Team Member';
        const avatarUrl = matter.employee_avatar || matter.employeeAvatar;
        const initials = leaderName
          .split(' ')
          .map((n: string) => n[0])
          .join('')
          .substring(0, 2)
          .toUpperCase();

        return (
          <div className="leader-cell">
            {avatarUrl ? (
              <img src={avatarUrl} alt={leaderName} className="leader-avatar" />
            ) : (
              <div className="leader-avatar-placeholder">{initials}</div>
            )}
            <div className="leader-info">
              <div className="leader-name">{leaderName}</div>
              <div className="leader-title">{leaderTitle}</div>
            </div>
          </div>
        );
      },
    },
    {
      header: <SortHeader label="Status" colKey="status" />,
      render: (matter: any) => {
        if (!matter.status) return <span className="table-na">N/A</span>;
        const statusLower = matter.status.toLowerCase();
        let variant: 'active' | 'at-risk' | 'in-review' | 'completed' | 'open' = 'open';
        if (statusLower.includes('active') || statusLower.includes('open')) {
          variant = 'active';
        } else if (statusLower.includes('risk') || statusLower.includes('overdue')) {
          variant = 'at-risk';
        } else if (statusLower.includes('review') || statusLower.includes('progress')) {
          variant = 'in-review';
        } else if (statusLower.includes('complete') || statusLower.includes('closed')) {
          variant = 'completed';
        }
        return <StatusBadge status={matter.status} variant={variant} size="small" />;
      },
    },
    {
      header: <SortHeader label="Due Date" colKey="duedate" />,
      render: (matter: any) => {
        const dueDateInfo = formatDueDate(matter.closedate || matter.due_date);
        const completed = isCompleted(matter);
        return (
          <div className="due-date-cell">
            <div
              className="due-date-text"
              style={{ color: completed ? '#28a745' : dueDateInfo.color }}
            >
              {dueDateInfo.date}
            </div>
            {completed ? (
              <div className="due-date-status" style={{ color: '#28a745' }}>
                Completed
              </div>
            ) : (
              <div className="due-date-status" style={{ color: dueDateInfo.color }}>
                {dueDateInfo.status}
              </div>
            )}
          </div>
        );
      },
    },
    {
      header: <SortHeader label="Open Date" colKey="opendate" />,
      render: (matter: any) =>
        matter.opendate
          ? new Date(matter.opendate).toLocaleDateString()
          : 'N/A',
    },
  ];

  return (
    <div className="StakeholderMatters">
      <div className="stakeholder-section-header">

      </div>
      <div className="stakeholder-summary-grid">
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">
              {isLegalEnvironment ? 'Total Matters' : 'Total Projects'}
            </div>
            <div className="stakeholder-summary-icon icon-muted">
              <FaListUl />
            </div>
          </div>
          <div className="stakeholder-summary-value">{summary.total}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">In Progress</div>
            <div className="stakeholder-summary-icon icon-blue">
              <FaCircleNotch />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-blue">{summary.inProgress}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Overdue</div>
            <div className="stakeholder-summary-icon icon-red">
              <FaExclamationTriangle />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-red">{summary.overdue}</div>
        </div>
        <div className="stakeholder-summary-card">
          <div className="stakeholder-summary-top">
            <div className="stakeholder-summary-label">Completed</div>
            <div className="stakeholder-summary-icon icon-green">
              <FaCheckCircle />
            </div>
          </div>
          <div className="stakeholder-summary-value summary-green">{summary.completed}</div>
        </div>
      </div>
      <div className="stakeholder-filters-row">
        <FilterDropdown
          label=""
          placeholder="All Statuses"
          options={[{ value: 'all', label: 'All Statuses' }, ...filterOptions.statuses]}
          value={statusFilter}
          onChange={(value) => setStatusFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Leaders"
          options={[{ value: 'all', label: 'All Leaders' }, ...filterOptions.leaders]}
          value={leaderFilter}
          onChange={(value) => setLeaderFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder={isLegalEnvironment ? 'All Matter Types' : 'All Project Types'}
          options={[
            { value: 'all', label: isLegalEnvironment ? 'All Matter Types' : 'All Project Types' },
            ...filterOptions.matterTypes,
          ]}
          value={typeFilter}
          onChange={(value) => setTypeFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-filter-dropdown"
        />
        <FilterDropdown
          label=""
          placeholder="All Dates"
          options={[
            { value: 'all', label: 'All Dates' },
            { value: 'today', label: 'Today' },
            { value: 'week', label: 'This Week' },
            { value: 'month', label: 'This Month' },
            { value: 'year', label: 'This Year' },
            { value: 'overdue', label: 'Overdue' },
          ]}
          value={dateFilter}
          onChange={(value) => setDateFilter(Array.isArray(value) ? value[0] : value)}
          className="stakeholder-filter-dropdown"
        />
      </div>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <Table
          columns={mattersColumns}
          data={displayedMatters}
          rowKey="matterid"
          emptyMessage={`No ${entitySearchTerm} found for this ${type === 'organization' ? 'organization' : 'individual'}`}
        />
      )}
    </div>
  );
};

export default StakeholderMatters;

